import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { MagnifyingGlass, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

export const PAGE_INDEX = [
  { group: "Daily work", label: "Dashboard", to: "/", kw: "home today overview" },
  { group: "Daily work", label: "Call Log", to: "/call-log", kw: "phone calls messages" },
  { group: "Daily work", label: "Calendar, scheduling & interviews", to: "/calendar", kw: "schedule shifts appointments interview" },
  { group: "Daily work", label: "Billing & prepay", to: "/billing", kw: "invoices payments ledger money private pay" },
  { group: "Daily work", label: "Uploads & scan documents", to: "/uploads", kw: "files scan import" },
  { group: "Daily work", label: "Notes", to: "/notes", kw: "shift notes visit notes" },
  { group: "Clients", label: "Client list", to: "/clients", kw: "clients roster" },
  { group: "Clients", label: "Client billing & prepay", to: "/billing", kw: "client billing invoices payments prepay ledger private pay money" },
  { group: "Clients", label: "New client intake", to: "/intake-form/new", kw: "admit admission referral" },
  { group: "Clients", label: "Send intake link", action: "/intake-start", kw: "application link family" },
  { group: "Clients", label: "Client handbook", to: "/client-handbook", kw: "agreement service" },
  { group: "Clients", label: "Print blank service agreement / signing packet", to: "/templates", kw: "print agreement receive services blank packet paper" },
  { group: "Clients", label: "Visits & documents", to: "/reconciliation", kw: "evv visits reconciliation" },
  { group: "Employees — hiring", label: "Applicants (hiring pipeline)", to: "/hiring", kw: "applicants candidates recruiting prescreen" },
  { group: "Employees — hiring", label: "Send application link", action: "/apply", kw: "dcw application link" },
  { group: "Employees — hiring", label: "References & verifications", to: "/references", kw: "reference checks verification" },
  { group: "Employees — hiring", label: "Hiring register — dates & every reference", to: "/hiring-register", kw: "references register application interview hire date" },
  { group: "Employees — manage", label: "Employee roster", to: "/users", kw: "staff caregivers employees team" },
  { group: "Employees — manage", label: "Onboarding", to: "/onboarding", kw: "new hire paperwork documents" },
  { group: "Employees — manage", label: "Orientation", to: "/orientation", kw: "first day orientation" },
  { group: "Training", label: "Task codes hub (everything per code)", to: "/task-codes", kw: "task codes care tasks hub 115 134 140 141" },
  { group: "Training", label: "Training library (all topics)", to: "/training?tab=topics", kw: "training courses ceu topics" },
  { group: "Training", label: "Training tracker (who's due)", to: "/training?tab=tracker", kw: "training due overdue compliance tracker" },
  { group: "Training", label: "Training questions", to: "/questions", kw: "quiz bank" },
  { group: "Training", label: "Training creator", to: "/training-creator", kw: "build course voiceover video narration avatar" },
  { group: "Employees — manage", label: "Meetings", to: "/meetings", kw: "inservice video" },
  { group: "Employees — manage", label: "Print center", to: "/print-center", kw: "print badges packets" },
  { group: "Employees — manage", label: "Employee files (audit books)", to: "/employee-files", kw: "audit book historical record locked file" },
  { group: "Employees — manage", label: "Forms", to: "/forms", kw: "blank forms" },
  { group: "Compliance", label: "Quality (CQI)", to: "/quality", kw: "quality improvement" },
  { group: "Compliance", label: "Audit Prep", to: "/audit", kw: "audit readiness" },
  { group: "Compliance", label: "Audit questionnaire", to: "/audit-questionnaire", kw: "licensure survey" },
  { group: "Compliance", label: "Payer Audits", to: "/payer-audits", kw: "insurance audit" },
  { group: "Compliance", label: "EVV", to: "/evv", kw: "electronic visit verification hhaexchange" },
  { group: "Compliance", label: "Policies", to: "/policies", kw: "policy manual" },
  { group: "Compliance", label: "Guidelines (incl. mission & operating principles)", to: "/guidelines", kw: "procedures guidelines mission values operating principles foundational philosophy" },
  { group: "Compliance", label: "Operational philosophy (mission, values & principles)", to: "/guidelines", kw: "philosophy mission values who we are principles" },
  { group: "Compliance", label: "Survey Answers", to: "/survey-answers", kw: "satisfaction surveys" },
  { group: "System", label: "Knowledge base", to: "/knowledge", kw: "how to help articles" },
  { group: "System", label: "Document library (all blank forms)", to: "/documents", kw: "blank forms print packet templates agreement service intake documents fill online" },
  { group: "System", label: "Employee form letters", to: "/templates", kw: "letters templates employee notice" },
  { group: "System", label: "Reports", to: "/reports", kw: "exports data" },
  { group: "System", label: "Assistant", to: "/assistant", kw: "ai helper ask" },
  { group: "System", label: "Notifications", to: "/notifications", kw: "alerts reminders" },
  { group: "System", label: "Settings", to: "/settings", kw: "preferences account office" },
  { group: "System", label: "Guide", to: "/guide", kw: "help tutorial" },
  { group: "Me", label: "My Profile", to: "/profile", kw: "my account" },
];

export function QuickFind() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [people, setPeople] = useState(null); // {clients, staff} — loaded lazily on first open
  const navigate = useNavigate();
  const groups = useMemo(() => [...new Set(PAGE_INDEX.map((p) => p.group))], []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open || people !== null) return;
    (async () => {
      const res = { clients: [], staff: [] };
      try { const { data } = await api.get("/clients"); res.clients = Array.isArray(data) ? data : []; } catch {}
      try { const { data } = await api.get("/users"); res.staff = Array.isArray(data) ? data : []; } catch {}
      setPeople(res);
    })();
  }, [open, people]);

  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchEvery = (...parts) => {
    const hay = parts.filter(Boolean).join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  };
  const clientHits = words.length && people
    ? people.clients.filter((c) => matchEvery(c.full_name || c.name, c.phone, c.email, c.client_code)).slice(0, 6) : [];
  const staffHits = words.length && people
    ? people.staff.filter((s) => matchEvery(s.name, s.email)).slice(0, 6) : [];

  const pick = (p) => {
    setOpen(false);
    if (p.action) {
      const url = `${window.location.origin}${p.action}`;
      try {
        if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
      } catch {}
      toast.success(`${p.label} — link copied`, { description: url, duration: 8000 });
    } else {
      navigate(p.to);
    }
  };

  const goPerson = (to) => { setOpen(false); navigate(to); };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="quick-find-button"
        className="hidden items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3.5 py-1.5 text-sm text-stone-400 transition-colors hover:border-stone-300 hover:text-stone-600 md:inline-flex">
        <MagnifyingGlass size={15} />
        <span>Find anything…</span>
        <kbd className="rounded border border-stone-200 bg-white px-1.5 text-[10px] font-semibold text-stone-400">⌘K</kbd>
      </button>
      <button onClick={() => setOpen(true)} aria-label="Search" data-testid="quick-find-button-mobile"
        className="flex h-9 w-9 items-center justify-center rounded-md border border-stone-300 text-stone-700 hover:bg-accent md:hidden">
        <MagnifyingGlass size={18} />
      </button>
      <CommandDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
        <CommandInput value={q} onValueChange={setQ} placeholder="Type a client's or employee's name, or a page / task…" data-testid="quick-find-input" />
        <CommandList data-testid="quick-find-list">
          <CommandEmpty>No matches — try a person's name, or a word like “billing”, “training”, “audit”.</CommandEmpty>
          {clientHits.length + staffHits.length > 0 && (
            <CommandGroup heading="People" data-testid="quick-find-people">
              {clientHits.map((c) => (
                <CommandItem key={`c-${c.id}`} value={`client ${c.full_name || c.name || ""} ${c.phone || ""} ${c.email || ""}`}
                  onSelect={() => goPerson(`/clients/${c.id}`)} data-testid={`quick-find-client-${c.id}`}>
                  {c.full_name || c.name}
                  <span className="ml-auto text-xs text-muted-foreground">client{c.status ? ` · ${c.status}` : ""}</span>
                </CommandItem>
              ))}
              {staffHits.map((s) => (
                <CommandItem key={`s-${s.id}`} value={`staff ${s.name || ""} ${s.email || ""}`}
                  onSelect={() => goPerson(`/team/${s.id}`)} data-testid={`quick-find-staff-${s.id}`}>
                  {s.name || s.email}
                  <span className="ml-auto text-xs text-muted-foreground">staff</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {groups.map((g) => (
            <CommandGroup key={g} heading={g}>
              {PAGE_INDEX.filter((p) => p.group === g).map((p) => (
                <CommandItem key={p.label} value={`${p.label} ${p.kw}`} onSelect={() => pick(p)}
                  data-testid={`quick-find-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {p.action ? <LinkSimple size={14} className="text-primary" /> : null}
                  {p.label}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
